import { Injectable } from '@angular/core';
import { BookingsService, DoorOpeningStatus } from './bookings.service';
import { ToastService } from './toast.service';
import { ApiError } from '../models/common/api-error';
import { TranslocoService } from '@jsverse/transloco';

/** Nombre de fois où l'on demande si le boîtier a confirmé, une fois par seconde. */
const CONFIRMATION_ATTEMPTS = 15;
const CONFIRMATION_INTERVAL_MS = 1000;

@Injectable({
  providedIn: 'root'
})
export class UnlockDoorService {
  constructor(
    private bookingsService: BookingsService,
    private toastService: ToastService,
    private translocoService: TranslocoService,
  ) {}

  /**
   * Demande l'ouverture, puis, pour une porte resivo, attend que le boîtier
   * confirme : la réponse de l'API ne dit que « commande acceptée ».
   */
  async unlockDoor(token: string): Promise<boolean> {
    try {
      await this.bookingsService.unlockDoor(token);
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      console.error(exception);
      return false;
    }

    const status = await this.waitForConfirmation(token);

    if (status === 'confirmed') {
      this.toastService.success(
        this.translocoService.translate('common.toast.door-opened'),
        this.translocoService.translate('common.toast.door-opened-title'),
        7500,
      );
    } else if (status === 'pending') {
      this.toastService.warning(
        this.translocoService.translate('common.toast.door-unconfirmed'),
        this.translocoService.translate('common.toast.door-unconfirmed-title'),
        15000,
      );
    } else {
      // Porte Exivo : aucune confirmation n'est attendue
      this.toastService.success(
        this.translocoService.translate('common.toast.door-opening'),
        this.translocoService.translate('common.toast.door-opening-title'),
        7500,
      );
    }

    return true;
  }

  private async waitForConfirmation(token: string): Promise<DoorOpeningStatus> {
    for (let attempt = 0; attempt < CONFIRMATION_ATTEMPTS; attempt++) {
      let status: DoorOpeningStatus;
      try {
        status = await this.bookingsService.doorOpeningStatus(token);
      } catch (exception) {
        // L'ordre est parti : faute de nouvelles, on s'en tient au message habituel
        console.error(exception);
        return attempt === 0 ? 'none' : 'pending';
      }

      if (status !== 'pending') {
        return status;
      }

      if (attempt === 0) {
        this.toastService.info(
          this.translocoService.translate('common.toast.door-opening'),
          this.translocoService.translate('common.toast.door-opening-title'),
          CONFIRMATION_ATTEMPTS * CONFIRMATION_INTERVAL_MS,
        );
      }

      await new Promise((resolve) => setTimeout(resolve, CONFIRMATION_INTERVAL_MS));
    }

    return 'pending';
  }
}
